/**
 * game-sdk 埋点模块：基于 StarTrackClient 封装游戏 SDK 相关埋点上报。
 */

import { createTrackClient, StarTrackClient } from '@web-publish-id/shared/track'
import { getSessionDuration, isDebugMode } from '@web-publish-id/shared/helper'
import { parseSeaartTokenPayload, parsePublishTokenPayload } from '@web-publish-id/shared/utils'
import { getCurrentLine, type ServiceType } from '@web-publish-id/shared/http'
import {
  GameTrackEvent,
  AuthSource,
  AuthStatus,
  HTTP_EVENTS,
  ADD_DURATION_PROPERTIES,
} from './events'
import type {
  SsoLoginStartParams,
  SsoLoginParams,
  PublishAccountLogoutParams,
  PublishTokenRefreshParams,
} from './types'

export { GameTrackEvent, AuthSource, AuthStatus }
export type { SsoLoginStartParams, SsoLoginParams, PublishAccountLogoutParams, PublishTokenRefreshParams }

// base_url 对应的服务类型
const SERVICE_TYPE: ServiceType = 'publish'

export class GameTracker {
  private client: StarTrackClient
  private ready = false

  constructor() {
    this.client = createTrackClient({
      debug: isDebugMode('debug'),
    })
  }

  async setup(): Promise<void> {
    await this.client.setup({
      storePrefix: 'game_sdk:',
    })
    this.ready = true
  }

  setCommonProperties(props: Record<string, any>) {
    this.client.setCommonProperties(props)
  }

  addCommonProperties(props: Record<string, any>) {
    this.client.addCommonProperties(props)
  }

  // —— SSO 登录 ——

  /** 发起 SSO 商户授权登录（API 调用前上报） */
  trackSsoLoginStart(sdkToken: string) {
    const payload = parseSeaartTokenPayload(sdkToken)
    const params: SsoLoginStartParams = {
      identify_id: payload.identify_id,
      identify_name: payload.name,
      auth_source: AuthSource.WEB,
    }
    this.event(GameTrackEvent.SSO_LOGIN_START, params)
  }

  /** SSO 登录成功 */
  trackSsoLoginSuccess(sdkToken: string, expiry_at: number, scopes: string) {
    const payload = parseSeaartTokenPayload(sdkToken)
    const params: SsoLoginParams = {
      identify_id: payload.identify_id,
      identify_name: payload.name,
      auth_source: AuthSource.WEB,
      auth_status: AuthStatus.SUCCESS,
      expiry_at,
      scopes,
      duration: getSessionDuration(),
    }
    this.event(GameTrackEvent.SSO_LOGIN, params)
  }

  /** SSO 登录失败 */
  trackSsoLoginFailed(sdkToken: string, errorMsg: string) {
    const payload = parseSeaartTokenPayload(sdkToken)
    const params: SsoLoginParams = {
      identify_id: payload.identify_id,
      identify_name: payload.name,
      auth_source: AuthSource.WEB,
      auth_status: AuthStatus.FAILED,
      auth_error_msg: errorMsg,
    }
    this.event(GameTrackEvent.SSO_LOGIN, params)
  }

  // —— 登出 ——

  /** 发行平台用户登出 */
  trackPublishAccountLogout(accessToken: string) {
    const payload = parsePublishTokenPayload(accessToken)
    const params: PublishAccountLogoutParams = {
      union_id: payload.union_id,
      open_id: payload.open_id,
      identify_id: payload.identify_id,
      identify_name: payload.name,
    }
    this.event(GameTrackEvent.PUBLISH_ACCOUNT_LOGOUT, params)
  }

  // —— Token 续期 ——

  /** 发行平台 token 续期（发起续期时上报） */
  trackPublishTokenRefresh(oldToken: string) {
    const payload = parsePublishTokenPayload(oldToken)
    const params: PublishTokenRefreshParams = {
      union_id: payload.union_id,
      open_id: payload.open_id,
      identify_id: payload.identify_id,
      identify_name: payload.name,
    }
    this.event(GameTrackEvent.PUBLISH_TOKEN_REFRESH, params)
  }

  /** 发行平台 token 续期失败 */
  trackPublishTokenRefreshFailed(oldToken: string, errorMsg: string) {
    const payload = parsePublishTokenPayload(oldToken)
    const params: PublishTokenRefreshParams = {
      union_id: payload.union_id,
      open_id: payload.open_id,
      identify_id: payload.identify_id,
      identify_name: payload.name,
      auth_error_msg: errorMsg,
    }
    this.event(GameTrackEvent.PUBLISH_TOKEN_REFRESH, params)
  }

  // —— 内部工具 ——

  private event(name: string, properties: Record<string, any>) {
    const mergeProperties: any = {}

    // 添加持续时间属性
    if (ADD_DURATION_PROPERTIES.includes(name as GameTrackEvent)) {
      mergeProperties.duration = getSessionDuration()
    }

    // 添加当前调用服务的 base_url
    if (HTTP_EVENTS.has(name as GameTrackEvent)) {
      mergeProperties.base_url = getCurrentLine(SERVICE_TYPE)
    }

    this.client.event(name, {
      ...mergeProperties,
      ...properties,
    })
  }
}
